import { Activity, TrendingDown, TrendingUp, Minus } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { MarketRegime } from '@/lib/scoring/types';

const PHASE_STYLE: Record<
  MarketRegime['phase'],
  { label: string; cls: string; icon: typeof Activity }
> = {
  risk_on_uptrend: {
    label: 'Aufwärtstrend',
    cls: 'bg-emerald-900/40 border-emerald-700/60 text-emerald-200',
    icon: TrendingUp,
  },
  risk_on_consolidation: {
    label: 'Trend, aber müde',
    cls: 'bg-blue-900/40 border-blue-700/60 text-blue-200',
    icon: TrendingUp,
  },
  transition: {
    label: 'Unentschlossen',
    cls: 'bg-amber-900/40 border-amber-700/60 text-amber-200',
    icon: Minus,
  },
  risk_off_correction: {
    label: 'Korrektur',
    cls: 'bg-orange-900/40 border-orange-700/60 text-orange-200',
    icon: TrendingDown,
  },
  risk_off_bear: {
    label: 'Abwärtstrend',
    cls: 'bg-red-900/40 border-red-700/60 text-red-200',
    icon: TrendingDown,
  },
};

type RegimeContext = 'stocks' | 'crypto' | 'bonds' | 'metals';

export function MarketRegimeBadge({
  regime,
  label,
  compact,
  invertedSemantics,
  context,
}: {
  regime: MarketRegime | null;
  label: string;
  compact?: boolean;
  /** Bei TLT (Bonds) bedeutet "Risk-On (steigt)" Risk-Off-Signal für Aktien.
   *  Wenn true, wird das Label entsprechend umgedeutet. */
  invertedSemantics?: boolean;
  /** Audit 2026-04-29: Klartext-Trading-Auswirkung pro Kontext-Phase. */
  context?: RegimeContext;
}) {
  if (!regime) {
    return (
      <span className="text-[11px] text-zinc-500 italic">
        {label}: Daten fehlen
      </span>
    );
  }
  const style = PHASE_STYLE[regime.phase];
  const Icon = style.icon;
  const labelText = invertedSemantics
    ? invertedLabel(regime.phase)
    : style.label;
  const tradingImpact = context ? tradingImpactFor(context, regime.phase) : null;

  return (
    <div
      className={cn(
        'inline-flex items-start gap-2 rounded-md border px-2.5 py-1.5',
        style.cls
      )}
      title={regime.notes}
    >
      <Icon className="size-3.5 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
        <div className="text-xs font-medium leading-tight">{labelText}</div>
        {!compact && (
          <>
            <div className="text-[10px] opacity-70 mt-0.5">{regime.notes}</div>
            {tradingImpact && (
              <div className="text-[10px] mt-1 leading-snug font-medium opacity-90 border-t border-current/20 pt-1">
                {tradingImpact}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Audit 2026-04-29 (U2.2): Klartext-Trading-Auswirkung pro Kontext-Phase.
 * Übersetzt "SPY über MA200" in eine konkrete Handelsanweisung.
 */
function tradingImpactFor(
  context: RegimeContext,
  phase: MarketRegime['phase']
): string {
  if (context === 'stocks') {
    switch (phase) {
      case 'risk_on_uptrend':
        return 'Aktien sind im Aufwärtstrend. Du kannst Trend-Trades aufmachen — solange der breite Markt (SPY) über dem 200-Tage-Schnitt bleibt.';
      case 'risk_on_consolidation':
        return 'Trend läuft, aber müde. Nur die besten Setups handeln, lieber kleinere Positionen oder in Stufen einsteigen.';
      case 'transition':
        return 'Markt ist unentschlossen. Mit neuen Käufen warten, bis sich die Richtung wieder klärt.';
      case 'risk_off_correction':
        return 'Korrektur. Keine neuen Trades aufmachen. Offene Positionen prüfen — Stops enger ziehen wenn sinnvoll.';
      case 'risk_off_bear':
        return 'Abwärtstrend. Keine neuen Käufe. Mehr Cash halten und auf bessere Zeiten warten.';
    }
  }
  if (context === 'crypto') {
    switch (phase) {
      case 'risk_on_uptrend':
        return 'Krypto läuft im Trend. Neue Käufe sind okay — aber kleinere Positionen wegen der starken Schwankungen.';
      case 'risk_on_consolidation':
        return 'Krypto bewegt sich seitwärts. Auf einen klaren Ausbruch mit hohem Volumen warten, sonst nicht handeln.';
      case 'transition':
        return 'Krypto-Markt unklar. Kein klarer Trend — lieber pausieren statt erzwingen.';
      case 'risk_off_correction':
        return 'Krypto-Korrektur. Neue Käufe nur in kleinen Positionen, mit klarer Bestätigung.';
      case 'risk_off_bear':
        return 'Krypto-Abwärtstrend. Nur Bitcoin und Ethereum sind für Langfrist-Aufbau geeignet.';
    }
  }
  if (context === 'bonds') {
    switch (phase) {
      case 'risk_on_uptrend':
      case 'risk_on_consolidation':
        return 'Anleihen steigen — das deutet auf fallende Zinsen hin. Wachstums-Aktien sollten besonders sorgfältig geprüft werden.';
      case 'transition':
        return 'Anleihen bewegen sich seitwärts. Aktien-Setups wie gewohnt bewerten.';
      case 'risk_off_correction':
      case 'risk_off_bear':
        return 'Anleihen fallen — Zinsen steigen, das macht Wachstums-Aktien teuer. Solche Setups doppelt prüfen.';
    }
  }
  if (context === 'metals') {
    switch (phase) {
      case 'risk_on_uptrend':
      case 'risk_on_consolidation':
        return 'Gold/Silber stark — oft ein Zeichen für Inflation oder Krise. Achtung wenn du schon stark in Energie- oder Rohstoff-Werten investiert bist.';
      case 'transition':
        return 'Edelmetalle bewegen sich seitwärts. Kein klares Signal vom Markt.';
      case 'risk_off_correction':
      case 'risk_off_bear':
        return 'Edelmetalle schwach — Markt erwartet keine Inflation/Krise. Eher gutes Umfeld für Risiko-Aktien.';
    }
  }
  return '';
}

function invertedLabel(phase: MarketRegime['phase']): string {
  switch (phase) {
    case 'risk_on_uptrend': return 'Schlecht für Aktien';
    case 'risk_on_consolidation': return 'Eher schlecht für Aktien';
    case 'transition': return 'Unentschlossen';
    case 'risk_off_correction': return 'Eher gut für Aktien';
    case 'risk_off_bear': return 'Gut für Aktien';
  }
}
